/**
 * Karotte — Jaeger (Musketeer). LMAO-Style (BATTLE-14).
 * Persoenlichkeit: "kerniger Scharfschuetze" — Kegel-Korpus mit Querrillen (dunkle
 * Ringe geben dem Kegel Volumen), Blattgruen als mehrschichtige BEFIEDERUNG
 * (Jaegerhut-Federbusch, nach hinten gebogen), Wangen-Narbe + Wurzel-Stoppeln.
 * Sternstufen: 2★ Leder-Guertel + gepanzerte Praezisions-Spitze (Metall-Ferrule).
 * 3★ goldene Zielmarken-Gravur (Reticle-Emblem), Lade-Runen Richtung Spitze,
 * Gold-Ferrule am Federbusch — pulsiert mit pose.castPulse.
 * Referenz-Qualitaet: kohlrabi.ts. Contract: species/types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, glow, lighten, rgba, rimLight, specular, volumeGradient } from '../draw3d'

/** Gold wie kohlrabi-3★-Runen (KEYWORD_COLORS.holy-Familie) — Praezisions-Gravuren */
const GOLD = '#ffd94a'
/** stilisiertes Metall wie kohlrabi-2★-Band (etablierte Palette) */
const METAL_HI = '#d7dee6'
const METAL_MID = '#9aa4ae'
const METAL_LO = '#6a737d'

/** gebogene Feder-/Blatt-Klinge: gefuellter Pfad aus zwei quadratischen Kurven.
 *  w = halbe Blattbreite (px), bend = seitliche Woelbung (px, Vorzeichen = Richtung) */
function leafBlade(
  ctx: CanvasRenderingContext2D,
  bx: number, by: number, tx: number, ty: number, w: number, bend: number,
): void {
  const dx = tx - bx
  const dy = ty - by
  const len = Math.hypot(dx, dy) || 1
  const px = -dy / len
  const py = dx / len
  const mx = bx + dx * 0.45
  const my = by + dy * 0.45
  ctx.beginPath()
  ctx.moveTo(bx, by)
  ctx.quadraticCurveTo(mx + px * (bend + w), my + py * (bend + w), tx, ty)
  ctx.quadraticCurveTo(mx + px * (bend - w), my + py * (bend - w), bx, by)
  ctx.closePath()
}

export const paintKarotte: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  ctx.save()
  ctx.lineWidth = Math.max(2, r * 0.12)
  ctx.strokeStyle = colors.outline
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'

  // Kegelspitze leicht Richtung Musketen-Lauf gebogen (Persoenlichkeit, deterministisch)
  const tipX = x + r * 0.1
  const tipY = y + r * 1.04

  // === 1) Federbusch (Blattgruen als Befiederung, HINTER dem Korpus) — 2 Schichten ===
  // Breite Feder-Klingen, als Fächer nach links-hinten geschwungen (Jaegerhut-Schmuck)
  const plumeBaseY = y - r * 0.72
  // [baseOffsetX, tipX, tipY, halbeBreite, bend, Farb-Shift, Vorder-Schicht?] (alles in r)
  const leaves: ReadonlyArray<readonly [number, number, number, number, number, number, boolean]> = [
    // Hinter-Schicht: dunkler + laenger, staerker nach hinten (links) gebogen
    [-0.08, -0.95, -1.5, 0.24, -0.36, -24, false],
    [0.0, -0.42, -1.75, 0.26, -0.26, -24, false],
    [0.08, 0.18, -1.6, 0.22, -0.06, -24, false],
    // Vorder-Schicht: heller + kuerzer, gleiche Schwungrichtung
    [-0.04, -0.6, -1.35, 0.19, -0.3, 0, true],
    [0.02, -0.08, -1.55, 0.2, -0.16, 12, true],
    [0.08, 0.36, -1.3, 0.16, 0.1, 0, true],
  ]
  for (const [bo, txr, tyr, w, bend, shift, front] of leaves) {
    const c = shift === 0 ? colors.accent : shift > 0 ? lighten(colors.accent, shift) : darken(colors.accent, -shift)
    const bx = x + bo * r
    const by = plumeBaseY
    const tx = x + txr * r
    const ty = y + tyr * r
    const g = ctx.createLinearGradient(bx, by, tx, ty)
    g.addColorStop(0, darken(c, 14))
    g.addColorStop(1, lighten(c, 10))
    ctx.fillStyle = g
    ctx.lineWidth = Math.max(1.5, r * 0.07)
    leafBlade(ctx, bx, by, tx, ty, w * r, bend * r)
    ctx.fill()
    ctx.stroke()
    // Mittelrippe entlang der Biegung (3★ vorne: goldene Praezisions-Ader)
    const goldVein = front && tier >= 3 && !dead
    ctx.save()
    ctx.strokeStyle = goldVein ? rgba(GOLD, 0.8) : rgba(lighten(c, 28), dead ? 0.3 : 0.7)
    ctx.lineWidth = Math.max(1, r * 0.04)
    const len = Math.hypot(tx - bx, ty - by) || 1
    const vpx = -(ty - by) / len
    const vpy = (tx - bx) / len
    ctx.beginPath()
    ctx.moveTo(bx + (tx - bx) * 0.1, by + (ty - by) * 0.1)
    ctx.quadraticCurveTo(
      bx + (tx - bx) * 0.45 + vpx * bend * r,
      by + (ty - by) * 0.45 + vpy * bend * r,
      tx, ty,
    )
    ctx.stroke()
    ctx.restore()
  }

  // === 2) Kegel-Korpus mit Volumen ===
  ctx.lineWidth = Math.max(2, r * 0.12)
  ctx.strokeStyle = colors.outline
  ctx.fillStyle = volumeGradient(ctx, x, y, r, colors.body)
  ctx.beginPath()
  ctx.moveTo(x - r * 0.78, y - r * 0.55)
  ctx.quadraticCurveTo(x, y - r * 1.04, x + r * 0.78, y - r * 0.55)
  ctx.quadraticCurveTo(x + r * 0.58, y + r * 0.45, tipX, tipY)
  ctx.quadraticCurveTo(x - r * 0.52, y + r * 0.45, x - r * 0.78, y - r * 0.55)
  ctx.closePath()
  ctx.fill()
  ctx.stroke()

  // Wurzel-Stoppeln an den Flanken ("kernig", Karotten-Charakteristik)
  ctx.save()
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(1.5, r * 0.055)
  ctx.beginPath()
  ctx.moveTo(x - r * 0.345, y + r * 0.5)
  ctx.lineTo(x - r * 0.5, y + r * 0.585)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(x + r * 0.33, y + r * 0.585)
  ctx.lineTo(x + r * 0.45, y + r * 0.7)
  ctx.stroke()
  ctx.restore()

  // === 3) Querrillen: dunkle Ringe + Licht-Kante darunter (Kegel-Volumen) ===
  // [yy, halbeBreite, Bogen, Mitte-Versatz (Spitzen-Biegung), alpha] (alles in r)
  const rings: ReadonlyArray<readonly [number, number, number, number, number]> = [
    [0.5, 0.38, 0.1, 0.02, 0.75],
    [0.72, 0.27, 0.08, 0.045, 0.75],
  ]
  ctx.save()
  for (const [yy, hw, bow, off, a] of rings) {
    const cx = x + off * r
    const ry0 = y + yy * r
    ctx.strokeStyle = rgba(colors.bodyDark, a)
    ctx.lineWidth = Math.max(1.5, r * 0.055)
    ctx.beginPath()
    ctx.moveTo(cx - hw * r, ry0)
    ctx.quadraticCurveTo(cx, ry0 + bow * r, cx + hw * r, ry0)
    ctx.stroke()
    // helle Kante unter der Rille = eingeschnittene Furche
    ctx.strokeStyle = rgba(lighten(colors.body, 22), dead ? 0.15 : a * 0.7)
    ctx.lineWidth = Math.max(1, r * 0.035)
    ctx.beginPath()
    ctx.moveTo(cx - hw * r * 0.94, ry0 + r * 0.055)
    ctx.quadraticCurveTo(cx, ry0 + (bow + 0.055) * r, cx + hw * r * 0.94, ry0 + r * 0.055)
    ctx.stroke()
  }
  ctx.restore()

  // Wangen-Narbe (rechte Seite, Blickrichtung Muskete) — Scharfschuetzen-Story im Standbild
  ctx.save()
  ctx.strokeStyle = rgba(darken(colors.body, 42), 0.85)
  ctx.lineWidth = Math.max(1.5, r * 0.045)
  ctx.translate(x + r * 0.4, y + r * 0.16)
  ctx.rotate(-0.65)
  for (const off of [-0.035, 0.035]) {
    ctx.beginPath()
    ctx.moveTo(off * r, -r * 0.08)
    ctx.lineTo(off * r, r * 0.08)
    ctx.stroke()
  }
  ctx.restore()

  // === 4) Sternstufen-Eskalation ===
  if (tier >= 2 && !dead) {
    // Leder-Guertel (Jaeger-Ausruestung) quer ueber den unteren Kegel
    const bTop = y + r * 0.48
    const bBot = y + r * 0.62
    const cx = x + r * 0.025
    const gBelt = ctx.createLinearGradient(0, bTop, 0, bBot)
    gBelt.addColorStop(0, darken(colors.body, 48))
    gBelt.addColorStop(1, darken(colors.body, 62))
    ctx.fillStyle = gBelt
    ctx.lineWidth = Math.max(1.5, r * 0.06)
    ctx.beginPath()
    ctx.moveTo(cx - r * 0.385, bTop)
    ctx.quadraticCurveTo(cx, bTop + r * 0.1, cx + r * 0.385, bTop)
    ctx.lineTo(cx + r * 0.335, bBot)
    ctx.quadraticCurveTo(cx, bBot + r * 0.09, cx - r * 0.335, bBot)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
    if (tier === 2) {
      // runde Metall-Ring-Schnalle (3★ ersetzt sie durch das Zielmarken-Emblem)
      const gBuck = ctx.createLinearGradient(0, y + r * 0.46, 0, y + r * 0.68)
      gBuck.addColorStop(0, METAL_HI)
      gBuck.addColorStop(0.5, METAL_MID)
      gBuck.addColorStop(1, METAL_LO)
      ctx.fillStyle = gBuck
      ctx.beginPath()
      ctx.arc(cx, y + r * 0.57, r * 0.115, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
      ctx.fillStyle = darken(colors.body, 58)
      ctx.beginPath()
      ctx.arc(cx, y + r * 0.57, r * 0.05, 0, Math.PI * 2)
      ctx.fill()
    }

    // gepanzerte Praezisions-Spitze (Metall-Ferrule auf dem Kegel-Ende)
    const capTop = y + r * 0.7
    const gCap = ctx.createLinearGradient(0, capTop, 0, tipY)
    gCap.addColorStop(0, METAL_HI)
    gCap.addColorStop(0.55, METAL_MID)
    gCap.addColorStop(1, METAL_LO)
    ctx.fillStyle = gCap
    ctx.beginPath()
    ctx.moveTo(x - r * 0.25, capTop)
    ctx.quadraticCurveTo(x - r * 0.13, y + r * 0.93, tipX, tipY)
    ctx.quadraticCurveTo(x + r * 0.21, y + r * 0.9, x + r * 0.28, capTop)
    ctx.quadraticCurveTo(x + r * 0.015, capTop + r * 0.1, x - r * 0.25, capTop)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
    // Metall-Glanzkante links
    ctx.save()
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)'
    ctx.lineWidth = Math.max(1, r * 0.045)
    ctx.beginPath()
    ctx.moveTo(x - r * 0.15, y + r * 0.76)
    ctx.quadraticCurveTo(x - r * 0.1, y + r * 0.88, x - r * 0.01, y + r * 0.97)
    ctx.stroke()
    ctx.restore()
  }

  if (tier >= 3 && !dead) {
    // Goldene Zielmarken-Gravur + Lade-Runen — pulsieren mit castPulse (docs/11 3★)
    const aG = 0.8 + pose.castPulse * 0.2
    ctx.save()
    ctx.strokeStyle = rgba(GOLD, aG)
    ctx.fillStyle = rgba(GOLD, aG)
    ctx.lineWidth = Math.max(1.5, r * 0.055)
    ctx.shadowColor = GOLD
    ctx.shadowBlur = r * (0.18 + 0.16 * pose.castPulse)
    // Reticle-Emblem am Guertel (Zielmarke = Jaeger-Praezision)
    const rcx = x + r * 0.025
    const rcy = y + r * 0.585
    const rr = r * 0.15
    ctx.beginPath()
    ctx.arc(rcx, rcy, rr, 0, Math.PI * 2)
    ctx.stroke()
    for (const [ax, ay] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      ctx.beginPath()
      ctx.moveTo(rcx + ax * rr * 0.7, rcy + ay * rr * 0.7)
      ctx.lineTo(rcx + ax * rr * 1.35, rcy + ay * rr * 1.35)
      ctx.stroke()
    }
    ctx.beginPath()
    ctx.arc(rcx, rcy, r * 0.046, 0, Math.PI * 2)
    ctx.fill()
    // Lade-Runen: drei Punkte wandern optisch zur Spitze (Praezisionsschuss laedt)
    for (const [dxr, dyr, dr2] of [[0.03, 0.8, 0.05], [0.055, 0.895, 0.042], [0.085, 0.975, 0.034]] as const) {
      ctx.beginPath()
      ctx.arc(x + dxr * r, y + dyr * r, dr2 * r, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.restore()
    // aufgeladene Spitze beim Cast
    if (pose.castPulse > 0.01) {
      glow(ctx, tipX, tipY, r * (0.3 + 0.35 * pose.castPulse), GOLD, 0.3 + 0.4 * pose.castPulse)
    }
  }

  // === 5) Ferrule am Federbusch-Ansatz (1★ Blattknoten / 2★ Metall / 3★ Gold) ===
  // Sitzt auf der Korpus-Oberkante, bindet den Busch optisch an den Koerper
  const fy = y - r * 0.74
  ctx.save()
  ctx.lineWidth = Math.max(1.5, r * 0.06)
  ctx.strokeStyle = colors.outline
  if (tier >= 3 && !dead) {
    const gF = ctx.createLinearGradient(0, fy - r * 0.11, 0, fy + r * 0.11)
    gF.addColorStop(0, lighten(GOLD, 18))
    gF.addColorStop(1, darken(GOLD, 28))
    ctx.fillStyle = gF
    ctx.shadowColor = GOLD
    ctx.shadowBlur = r * (0.12 + 0.1 * pose.castPulse)
  } else if (tier === 2 && !dead) {
    const gF = ctx.createLinearGradient(0, fy - r * 0.11, 0, fy + r * 0.11)
    gF.addColorStop(0, METAL_HI)
    gF.addColorStop(1, METAL_LO)
    ctx.fillStyle = gF
  } else {
    ctx.fillStyle = darken(colors.accent, 10)
  }
  ctx.beginPath()
  ctx.ellipse(x, fy, r * 0.24, r * 0.11, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.shadowBlur = 0
  ctx.stroke()
  if (!dead) {
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)'
    ctx.lineWidth = Math.max(1, r * 0.035)
    ctx.beginPath()
    ctx.ellipse(x, fy, r * 0.17, r * 0.065, 0, Math.PI * 1.1, Math.PI * 1.7)
    ctx.stroke()
  }
  ctx.restore()

  // === 6) Licht: Rim oben-links, Flanken-Licht den Kegel hinunter, Specular ===
  if (!dead) {
    rimLight(ctx, x, y - r * 0.05, r * 0.7, r * 0.78, 'rgba(255, 252, 240, 0.42)', Math.max(1.5, r * 0.07))
    ctx.save()
    ctx.strokeStyle = 'rgba(255, 250, 235, 0.28)'
    ctx.lineWidth = Math.max(1.5, r * 0.055)
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(x - r * 0.6, y - r * 0.3)
    ctx.quadraticCurveTo(x - r * 0.46, y + r * 0.28, x - r * 0.13, y + r * 0.78)
    ctx.stroke()
    ctx.restore()
    specular(ctx, x - r * 0.3, y - r * 0.5, r * 0.14, 0.5)
  }

  ctx.restore()
}
